import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery, useMutation, type MutationHookOptions } from '@apollo/client';
import { Device } from '../../lib/types';
import type { DeviceSort, SortDir } from '../../lib/types';
import { findFosterHomeForDevice, type FosterHome } from '../../lib/fosterAdapters';
import { useRescueContext } from '../../lib/RescueContext';
import ScheduledHandoffsModal from './ScheduledHandoffsModal';
import { formatScheduledFor, type ScheduledFor } from '../../lib/scheduling';
import { useToast } from '../../components/Toast/ToastContext';
import {
  RescueDeviceStatsData,
  rescueDeviceStatsQuery,
  RescueDevicesProgramData,
  rescueDevicesProgramQuery,
  RescueOrderShipmentsData,
  rescueOrderShipmentsQuery,
  rescueCreatePetFosterTransferMutation,
  rescueScheduleHandoffMutation,
  rescueRecallPetFosterTransferMutation,
  unassignDeviceMutation,
  rescueCancelPetAdoptionTransferMutation,
  rescueMarkAdoptionTransferNotRecoveredMutation,
  rescueMarkAdoptionTransferRecoveredMutation,
  rescueMarkAdoptionTransferStalledMutation,
  rescueSendSmsMutation,
  rescueCreateFosterHomeMutation,
  rescueArchiveFosterHomeMutation,
  type RescueCreateFosterHomeInput,
  type RescueCreateFosterHomeData,
  rescueUnassignedDeviceInventoryQuery,
  RescueUnassignedDeviceInventoryData,
} from '../../lib/rescueQueries';
import useRescueDevices from '../../hooks/useRescueDevices';
import { clampInventoryPage, inventoryQueryInput, inventoryTotalPages } from '../../lib/inventoryPaging';
import useStaleCut from '../../hooks/useStaleCut';
import useFosterRoster from '../../hooks/useFosterRoster';
import usePagedFosterHomes, { type RosterSortKey } from '../../hooks/usePagedFosterHomes';
import { isFosterContactComplete, SEARCH_DEBOUNCE_MS } from '../../lib/utils';
import SearchBar from '../../components/SearchBar/SearchBar';
import Button from '../../components/Button/Button';
import PortalTabs, { TabDef } from '../../components/PortalTabs/PortalTabs';
import StockTable from './StockTable';
import DeviceSectionTable from './DeviceSectionTable';
import type { DeviceSortKey } from './DeviceSectionTable';
import DevicesInTransitTable from './DevicesInTransitTable';
import PendingTransfersTable from './PendingTransfersTable';
import type { PendingSortKey } from './PendingTransfersTable';
import StalledTransfersTable from './StalledTransfersTable';
import type { StalledSortKey } from './StalledTransfersTable';
import MarkRecoveredModal from './MarkRecoveredModal';
import NotRecoveredModal from './NotRecoveredModal';
import CompletedTransfersTable from './CompletedTransfersTable';
import AddPetModal from './AddPetModal';
import AddDeviceToPetModal from './AddDeviceToPetModal';
import TransferToFosterModal, {
  type FosterTransferInput,
} from '../../components/TransferToFosterModal/TransferToFosterModal';
import RecallDeviceModal from '../../components/RecallDeviceModal/RecallDeviceModal';
import AdoptFromFosterModal from './AdoptFromFosterModal';
import FosterRosterTable from './FosterRosterTable';
import FosterHomeDetailsModal from './FosterHomeDetailsModal';
import ViewPetModal from '../Dashboard/ViewPetModal';
import AddFosterModal from './AddFosterModal';
import TransferToAdopterModal from '../../components/TransferToAdopterModal/TransferToAdopterModal';
import ResendEmailModal from './ResendEmailModal';
import CancelTransferModal, { CancellationReason } from './CancelTransferModal';
import WrongAdopterInfoModal from './WrongAdopterInfoModal';
import DeviceReturnedModal from './DeviceReturnedModal';
import DeviceKeptModal from './DeviceKeptModal';
import TestingCancelModal from './TestingCancelModal';
import PetAndDeviceReturnedCancelModal from './PetAndDeviceReturnedCancelModal';
import SendSmsModal from './SendSmsModal';
import OrderDevicesModal from './OrderDevicesModal';
import DevicesEnrollModal from './DevicesEnrollModal';
import EnrollEmptyState from '../../components/EnrollEmptyState/EnrollEmptyState';
import {
  trackDeviceTransferToFoster,
  trackDeviceTransferToAdopt,
  trackDeviceRecalled,
  trackDeviceResendEmail,
  trackDeviceCancelTransfer,
  trackDeviceCancelTransferFailed,
  trackDeviceMarkedRecovered,
  trackDeviceMarkedNotRecovered,
  trackDeviceMarkedStalled,
  trackDeviceSmsSent,
  trackDevicesOrderClicked,
  trackChipOwnerAdded,
  trackFosterHomeCreated,
  trackFosterHomeArchived,
  trackHandoffScheduled,
} from '../../analytics';
import { useTransferPetWithChipToAdopter } from '../../lib/useTransferPetWithChipToAdopter';
import styles from './Devices.module.scss';

/**
 * Every mutation on the page refetches the active queries so the summary cards and the
 * paginated sections refresh. Every section now renders a page the server chose, so a
 * refetch re-runs each one with the variables it is already watching and the row lands
 * wherever the server now puts it. No section holds an accumulated list for a refetch to
 * collapse into skeletons, which is what the old per-mutation sparing rules protected.
 *
 * Nothing behind the page drains `rescueDevices` a page at a time any more. The foster roster
 * stopped when a placement's pet became a click-time fetch, and the adopted-pet gate stopped
 * when it moved onto the row it describes — so a refetch has no accumulated list to reset, and
 * none of the half-gathered state the old sparing rules were protecting.
 */
const refetchActiveOpts: MutationHookOptions = { refetchQueries: 'active' };

/** Default page size for the server-side paginated sections. */
const PAGE_SIZE = 10;

/**
 * What the Stalled section's copy says before `rescueDeviceStats` has answered. The server
 * owns the real number — this only has to match the schema's documented default so the copy
 * does not flicker onto a different window on first paint.
 */
const DEFAULT_STALLED_TRANSFER_DAYS = 7;

/**
 * The Fosters device section opens on newest placement first — the pets that moved most
 * recently are the ones a volunteer is still following up on. The user can re-sort from
 * any of that table's headers; there is no unsorted state to return to.
 */
const DEFAULT_FOSTER_SORT: DeviceSort = { key: 'placed', dir: 'desc' };

/** Show completed adoptions from the last 90 days. */
const COMPLETED_SINCE = (() => {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  return d.toISOString();
})();

/** Top-level tabs. */
type DeviceTab = 'devices' | 'fosters' | 'adoptions';

const DEVICE_TABS: DeviceTab[] = ['devices', 'fosters', 'adoptions'];

/** Reads the `tab` query param, falling back to Devices for anything unrecognized. */
function parseDeviceTab(value: string | null): DeviceTab {
  return DEVICE_TABS.find((tab) => tab === value) ?? 'devices';
}

/** Sub-filters for the Devices tab, narrowing which panels are shown. */
type DeviceFilter = 'all' | 'stock' | 'activated' | 'inTransit';

const DEVICE_FILTERS: { key: DeviceFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'activated', label: 'Activated' },
  { key: 'stock', label: 'Stock' },
  { key: 'inTransit', label: 'In Transit' },
];

function LoadingSpinner({ message }: { message?: string }) {
  return (
    <div className={styles.loadingContainer}>
      <svg className={styles.spinner} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
        <path fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
      </svg>
      {message && <span>{message}</span>}
    </div>
  );
}

export default function Devices() {
  const { orgName } = useRescueContext();
  // `cache-and-network` — matching the Dashboard's card — because `pendingDeviceOrder` gates the
  // Order Devices button and only ops clears it, off in Slack. Under cache-first a rescue who was
  // blocked stays blocked on this page until a hard reload, long after their order was approved.
  const {
    data: enrollmentData,
    loading: enrollmentLoading,
    refetch: refetchEnrollment,
  } = useQuery<RescueDevicesProgramData>(rescueDevicesProgramQuery, { fetchPolicy: 'cache-and-network' });
  // Under `cache-and-network` every revalidation re-raises `loading` even though cached data is
  // already on screen, so "still loading" has to mean "loading with nothing to show" — otherwise a
  // background refetch flashes the page spinner and closes the enroll modal mid-revalidate.
  const enrollmentPending = enrollmentLoading && !enrollmentData;
  const isEnrolled = enrollmentData?.rescueDevicesProgram.enrolled ?? false;
  // An order the rescue has filed that Fi hasn't approved or rejected yet. Only one may be
  // outstanding, so while this is set the order CTA is closed off and the header explains why —
  // `rescueCreateDeviceOrder` would reject a second order anyway, and a form that can't be filed is
  // worse than no form.
  const pendingDeviceOrder = enrollmentData?.rescueDevicesProgram.pendingDeviceOrder ?? null;
  const { showToast } = useToast();
  const location = useLocation();
  const history = useHistory();
  const urlParams = new URLSearchParams(location.search);
  const urlSearch = urlParams.get('search') || '';
  const urlTab = parseDeviceTab(urlParams.get('tab'));
  const [searchInput, setSearchInput] = useState(urlSearch);
  const [searchQuery, setSearchQuery] = useState(urlSearch);
  const [currentTab, setCurrentTab] = useState<DeviceTab>(urlTab);
  const [deviceFilter, setDeviceFilter] = useState<DeviceFilter>('all');
  // Backs the assign-to-foster picker, the placements table's per-row home, and the details
  // overlay — all of which need every home, so only Adoptions can skip it.
  const roster = useFosterRoster({ enabled: currentTab !== 'adoptions', visible: currentTab === 'fosters' });
  const fosterHomes = roster.homes;
  // The roster *table*, which renders a page the server ordered. Null until the user clicks a
  // header, which asks for the default newest-home-first order.
  const [rosterSort, setRosterSort] = useState<{ key: RosterSortKey; dir: SortDir } | null>(null);
  const rosterPage = usePagedFosterHomes(PAGE_SIZE, searchQuery, rosterSort, { skip: currentTab !== 'fosters' });

  function handleRosterSort(key: RosterSortKey) {
    setRosterSort((prev) => {
      if (prev && prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      // Counts open on the largest — "who is fostering right now" and "who has taken the
      // most" are the questions worth a click; names open A–Z.
      return { key, dir: key === 'foster' ? 'asc' : 'desc' };
    });
  }
  const { dismissOverlays: dismissRosterOverlays } = roster;
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // Sync local input when URL changes externally (e.g. navigating from dashboard). Also drop the
  // roster overlays: a pet profile's device card links back to this page with a search on it, and
  // the row it lands on would otherwise sit behind the modal that opened it.
  useEffect(() => {
    setSearchInput(urlSearch);
    setSearchQuery(urlSearch);
    dismissRosterOverlays();
  }, [urlSearch, dismissRosterOverlays]);

  // Follow the `tab` param when the URL changes externally: the dashboard links a pet's device
  // here with the tab that lists it, since the Devices tab covers neither placements nor adoptions.
  useEffect(() => {
    setCurrentTab(urlTab);
  }, [urlTab]);

  function handleTabChange(key: string) {
    const tab = parseDeviceTab(key);
    setCurrentTab(tab);
    const params = new URLSearchParams(location.search);
    if (tab === 'devices') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    history.replace({ search: params.toString() });
  }

  // The page owns the only search debounce: `searchInput` drives the field, and `searchQuery`
  // — what every query on the page reads — lands one delay later, together with the URL param.
  // Neither the paginated hook nor the drains debounce again, so they all switch searches on
  // the same tick.
  function handleSearchChange(value: string) {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(value);
      const params = new URLSearchParams(location.search);
      if (value) {
        params.set('search', value);
      } else {
        params.delete('search');
      }
      history.replace({ search: params.toString() });
    }, SEARCH_DEBOUNCE_MS);
  }

  // ── Org name (from context — resolves chipReseller or RESCUE household) ──

  const resellerName = orgName;

  // ── Stats (unfiltered counts for summary cards) ──

  const { data: statsData } = useQuery<RescueDeviceStatsData>(rescueDeviceStatsQuery, {
    fetchPolicy: 'cache-and-network',
    variables: { completedSince: COMPLETED_SINCE },
  });
  const stats = statsData?.rescueDeviceStats;
  const scheduledCount = stats?.scheduledHandoffsCount ?? 0;
  // The same number the server's `stalled` predicate uses, so the Stalled section's copy can
  // never describe a window different from the one that decided which rows are in it. Falls
  // back to the schema's documented default for the tick before the stats query answers.
  const stalledThresholdDays = stats?.stalledTransferThresholdDays ?? DEFAULT_STALLED_TRANSFER_DAYS;

  // ── Outstanding shipments (Devices in Transit) ──

  const { data: shipmentsData } = useQuery<RescueOrderShipmentsData>(rescueOrderShipmentsQuery);
  const shipments = shipmentsData?.rescueOrderShipments ?? [];

  // ── Unassigned Device Inventory ───────────
  //
  // The `rescueUnassignedDeviceInventory` query returns only delivered devices
  // not attached to a pet, filtered and paged server-side. Search goes through
  // input.query so the Unassigned section honors the search bar like Rescue /
  // Foster do. The table has no sortable columns, so a page is all it needs.

  // Searching re-filters the whole list, so the page the user is on indexes into a different
  // one and has to go back to the first. Adjusted during render rather than in an effect
  // because the query input is built from it a few lines down, in this same render — an
  // effect lands a render later, after a request has already gone out at the old offset.
  const [stockPage, setStockPage] = useState(1);
  const [prevStockSearch, setPrevStockSearch] = useState(searchQuery);
  let currentStockPage = stockPage;
  if (prevStockSearch !== searchQuery) {
    setPrevStockSearch(searchQuery);
    setStockPage(1);
    currentStockPage = 1;
  }

  const inventoryInput = useMemo(
    () => inventoryQueryInput(searchQuery, currentStockPage, PAGE_SIZE),
    [searchQuery, currentStockPage],
  );

  const {
    data: inventoryData,
    previousData: previousInventoryData,
    loading: inventoryLoading,
    error: inventoryError,
    refetch: refetchInventory,
  } = useQuery<RescueUnassignedDeviceInventoryData>(rescueUnassignedDeviceInventoryQuery, {
    variables: { input: inventoryInput },
    notifyOnNetworkStatusChange: true,
  });

  // ── Section sorting ───────────────────────
  //
  // Both device sections order server-side: the column and direction ride along in the query
  // variables, so a sort spans the whole result set and the page the user is looking at is
  // already the right slice of it. `activatedSort` is null until the user clicks a header,
  // which asks for the server's own default (pet name ascending); the Fosters section has no
  // unsorted state to return to and opens on `DEFAULT_FOSTER_SORT`.
  const [activatedSort, setActivatedSort] = useState<DeviceSort | null>(null);
  const [fosterSort, setFosterSort] = useState<DeviceSort>(DEFAULT_FOSTER_SORT);

  // ── Server-side paginated sections ────────

  const rescue = useRescueDevices('IN_RESCUE', searchQuery, PAGE_SIZE, resellerName, undefined, {
    sortKey: activatedSort?.key ?? null,
    sortDir: activatedSort?.dir,
  });
  const completed = useRescueDevices('COMPLETED_ADOPTIONS', searchQuery, PAGE_SIZE, resellerName, COMPLETED_SINCE);
  // Live on every tab, not just Fosters: it is one page of rows, and the Fosters badge reads
  // its total, which has to be right before the tab is ever opened.
  const foster = useRescueDevices('IN_FOSTER', searchQuery, PAGE_SIZE, resellerName, undefined, {
    sortKey: fosterSort.key,
    sortDir: fosterSort.dir,
  });

  // ── Pending adoptions (two server-side halves) ──
  //
  // "Pending Transfers" and "Stalled Transfers" are the same filter narrowed by the server's
  // own `stalled` predicate — a transfer the rescue flagged, or one that has aged past
  // `stalledTransferThresholdDays`. Two independently paginated queries rather than one
  // drained list partitioned here, so neither section has to hold every transfer to know how
  // many it has. Their totals sum to the `pendingAdoptionsCount` the summary card reports,
  // which is the same filter with no flag at all.
  // STALLED sorts on age since the transfer was filed, so ascending is freshest-first: the
  // adoptions that just crossed the threshold lead, while the long-dead ones sink.
  const [stalledSort, setStalledSort] = useState<{ key: StalledSortKey; dir: SortDir }>({
    key: 'stalled',
    dir: 'asc',
  });
  // Null until the user clicks a header, which asks for the server's default newest-first order.
  const [pendingSort, setPendingSort] = useState<{ key: PendingSortKey; dir: SortDir } | null>(null);

  const pending = useRescueDevices('PENDING_ADOPTIONS', searchQuery, PAGE_SIZE, resellerName, undefined, {
    stalled: false,
    sortKey: pendingSort?.key ?? null,
    sortDir: pendingSort?.dir,
  });
  const stalled = useRescueDevices('PENDING_ADOPTIONS', searchQuery, PAGE_SIZE, resellerName, undefined, {
    stalled: true,
    sortKey: stalledSort.key,
    sortDir: stalledSort.dir,
  });

  // Toggle direction when re-clicking the active column; otherwise start a new column
  // ascending. Each hook resets its own section to page 1 when the sort changes.
  function handleStalledSort(key: StalledSortKey) {
    setStalledSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  }

  function handlePendingSort(key: PendingSortKey) {
    setPendingSort((prev) =>
      prev && prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  }

  // First click on a column starts it ascending; re-clicking the active column toggles
  // direction. Both hooks jump their own section back to page 1 when the sort changes, so
  // the new top row is what the user lands on.
  function handleActivatedSort(key: DeviceSortKey) {
    setActivatedSort((prev) =>
      prev && prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );
  }

  function handleFosterSort(key: DeviceSortKey) {
    setFosterSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      // Dates open on newest first (the question is "what moved recently"); the
      // name/ID columns open A–Z, matching the Activated section.
      return { key, dir: key === 'placed' ? 'desc' : 'asc' };
    });
  }

  // ── Unassigned devices ──
  //
  // The query is named `rescueUnassignedDeviceInventory` and only returns
  // devices not attached to a pet, so no client-side filter is needed.

  // `data` is undefined while a page is in flight, so read through the last successful result
  // the way the paginated device sections do. The count is not just this table's header: it
  // also feeds the Devices tab badge and StockTable's `totalCount === 0` skeleton switch, so
  // letting it fall to zero mid-fetch would blink the badge for every other section too.
  // Not when the fetch failed for a cut it has never answered: the retained page belongs to the
  // search before this one, and StockTable's error row yields to rows, so keeping them would
  // show the unfiltered stock as the search result and hide the failure. See useStaleCut.
  const stockStale = useStaleCut(JSON.stringify(inventoryInput), !!inventoryData);
  const stockResult =
    stockStale && inventoryError
      ? undefined
      : (inventoryData ?? previousInventoryData)?.rescueUnassignedDeviceInventory;
  const unassignedDevices = useMemo(() => stockResult?.items ?? [], [stockResult]);
  // Read from the server rather than the array length, which only agreed with it
  // while the list was drained.
  const unassignedCount = stockResult?.totalCount ?? 0;
  const stockTotalPages = inventoryTotalPages(unassignedCount, PAGE_SIZE);

  // Assigning the last device on the last page shrinks the list under the user — the count
  // drops, the page they are on no longer exists, and nothing else moves them off it. Clamped
  // here rather than in an effect so the empty page is never committed: React re-runs this
  // render with the corrected page instead of painting a blank table and fixing it after.
  const clampedStockPage = clampInventoryPage(currentStockPage, stockTotalPages);
  if (clampedStockPage !== currentStockPage) {
    setStockPage(clampedStockPage);
    currentStockPage = clampedStockPage;
  }

  // ── Loading / Error states ────────────────

  // Only show the full-page loading spinner on initial page load (no search active).
  // During search, Apollo may briefly have empty data for new variables — we don't
  // want to unmount the search bar (which would steal focus from the input).
  const initialLoading =
    !searchQuery &&
    rescue.loading &&
    foster.loading &&
    pending.loading &&
    rescue.devices.length === 0 &&
    foster.devices.length === 0 &&
    pending.devices.length === 0;
  const hasError = rescue.error && foster.error && pending.error && inventoryError;

  // ── T2A Stats ──────────────────────────

  const pendingCount = stats?.pendingAdoptionsCount ?? 0;
  const completedCount = stats?.completedAdoptionsCount ?? 0;
  const totalTransfers = pendingCount + completedCount;
  const showStats = totalTransfers > 0;

  const allTimeRate = totalTransfers > 0 ? Math.round((completedCount / totalTransfers) * 100) : 0;

  function rateColorClass(pct: number): string {
    if (pct >= 80) {
      return styles.summaryValueGreen;
    }
    if (pct >= 60) {
      return styles.summaryValueAmber;
    }
    return styles.summaryValueRed;
  }

  // ── GraphQL mutations ─────────────────────

  // Every section on this page is a page of rows the server chose, so every mutation can take
  // the same blanket refetch: re-running an active query re-runs it with the variables it is
  // watching — the page the user is on — and the row lands wherever the server now puts it.
  // Flagging a transfer as stalled relies on exactly that: the row has to move out of Pending
  // and into Stalled, which is a different query, not an edit to the row it was in.
  const [createFosterTransferMut] = useMutation(rescueCreatePetFosterTransferMutation, refetchActiveOpts);
  const [scheduleHandoffMut] = useMutation(rescueScheduleHandoffMutation, refetchActiveOpts);
  const [recallFosterTransfer] = useMutation(rescueRecallPetFosterTransferMutation, refetchActiveOpts);
  const [unassignDevice] = useMutation(unassignDeviceMutation, refetchActiveOpts);
  const [createFosterHome] = useMutation<RescueCreateFosterHomeData>(rescueCreateFosterHomeMutation, refetchActiveOpts);
  const [archiveFosterHome] = useMutation(rescueArchiveFosterHomeMutation, refetchActiveOpts);
  const [cancelAdoptionTransferMut] = useMutation(rescueCancelPetAdoptionTransferMutation, refetchActiveOpts);
  const [markNotRecoveredMut] = useMutation(rescueMarkAdoptionTransferNotRecoveredMutation, refetchActiveOpts);
  const [markRecoveredMut] = useMutation(rescueMarkAdoptionTransferRecoveredMutation, refetchActiveOpts);
  const [markStalledMut] = useMutation(rescueMarkAdoptionTransferStalledMutation, refetchActiveOpts);
  const [sendSmsMut] = useMutation(rescueSendSmsMutation);
  const transferPetWithChip = useTransferPetWithChipToAdopter(refetchActiveOpts);

  /** Reset all sections to page 1 after a mutation. */
  function resetAllPages() {
    rescue.resetPage();
    foster.resetPage();
    pending.resetPage();
    stalled.resetPage();
    completed.resetPage();
  }

  // ── Helper: find device across all sections ──

  function findDevice(deviceId: string): Device | undefined {
    return (
      rescue.devices.find((d) => d.id === deviceId) ??
      foster.devices.find((d) => d.id === deviceId) ??
      pending.devices.find((d) => d.id === deviceId) ??
      stalled.devices.find((d) => d.id === deviceId) ??
      completed.devices.find((d) => d.id === deviceId)
    );
  }

  // ── Modal state ──────────────────────────

  const [addPetOpen, setAddPetOpen] = useState(false);
  const [addPetDevice, setAddPetDevice] = useState<Device | null>(null);
  const [fosterOpen, setFosterOpen] = useState(false);
  const [fosterDevice, setFosterDevice] = useState<Device | null>(null);
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallDevice, setRecallDevice] = useState<Device | null>(null);
  const [adopterOpen, setAdopterOpen] = useState(false);
  const [adopterDevice, setAdopterDevice] = useState<Device | null>(null);
  const [adoptFromFosterOpen, setAdoptFromFosterOpen] = useState(false);
  const [scheduledOpen, setScheduledOpen] = useState(false);
  const [scheduledFocusId, setScheduledFocusId] = useState<string | null>(null);
  const [scheduledFocusAction, setScheduledFocusAction] = useState<'reschedule' | 'cancel'>('reschedule');
  const [adoptFromFosterDevice, setAdoptFromFosterDevice] = useState<Device | null>(null);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendDevice, setResendDevice] = useState<Device | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelDevice, setCancelDevice] = useState<Device | null>(null);
  // When the cancel flow is opened from the Stalled table the transfer is
  // already stalled, so we hide the "move to Stalled" reason (see below).
  const [cancelHideStalledOption, setCancelHideStalledOption] = useState(false);
  const [reenterOpen, setReenterOpen] = useState(false);
  const [reenterDevice, setReenterDevice] = useState<Device | null>(null);
  const [returnedOpen, setReturnedOpen] = useState(false);
  const [returnedDevice, setReturnedDevice] = useState<Device | null>(null);
  const [keptOpen, setKeptOpen] = useState(false);
  const [keptDevice, setKeptDevice] = useState<Device | null>(null);
  const [testingOpen, setTestingOpen] = useState(false);
  const [testingDevice, setTestingDevice] = useState<Device | null>(null);
  const [petDeviceReturnedOpen, setPetDeviceReturnedOpen] = useState(false);
  const [petDeviceReturnedDevice, setPetDeviceReturnedDevice] = useState<Device | null>(null);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsDevice, setSmsDevice] = useState<Device | null>(null);
  const [markRecoveredOpen, setMarkRecoveredOpen] = useState(false);
  const [markRecoveredDevice, setMarkRecoveredDevice] = useState<Device | null>(null);
  const [notRecoveredOpen, setNotRecoveredOpen] = useState(false);
  const [notRecoveredDevice, setNotRecoveredDevice] = useState<Device | null>(null);
  const [assignPetOpen, setAssignPetOpen] = useState(false);
  const [assignPetModuleId, setAssignPetModuleId] = useState<string | null>(null);
  const [orderDevicesOpen, setOrderDevicesOpen] = useState(false);
  // Modal opens by default for users who haven't enrolled in the devices
  // program. Once dismissed it stays closed for the rest of the page lifetime;
  // the empty state's CTA re-opens it.
  const [enrollModalDismissed, setEnrollModalDismissed] = useState(false);
  const enrollModalOpen = !enrollmentPending && !isEnrolled && !enrollModalDismissed;
  // ── Modal handlers ──────────────────────────

  function handleOpenAddPet(device: Device) {
    setAddPetDevice(device);
    setAddPetOpen(true);
  }

  function handleOpenFoster(device: Device) {
    setFosterDevice(device);
    setFosterOpen(true);
  }

  function handleOpenRecall(device: Device) {
    setRecallDevice(device);
    setRecallOpen(true);
  }

  function handleOpenAdopter(device: Device) {
    setAdopterDevice(device);
    setAdopterOpen(true);
  }

  function handleOpenAdoptFromFoster(device: Device) {
    setAdoptFromFosterDevice(device);
    setAdoptFromFosterOpen(true);
  }

  /** Reuse-first: an existing contact comes back refreshed, which the modal has already warned about. */
  async function handleCreateFosterHome(input: RescueCreateFosterHomeInput) {
    // Captured before the write, since the mutation refetches active queries and the roster is one.
    const knownIds = new Set(fosterHomes.homes.map((home) => home.id));
    const { data } = await createFosterHome({ variables: { input } });

    // Only a genuinely new record is a creation. The mutation returns the home the contact mapped
    // to, so an id already on the roster means it refreshed one rather than added one — counting
    // that would inflate the roster's growth with edits. Safe to judge from the roster here: this
    // handler is only reachable from the Fosters tab, which has it loaded to render the table.
    const saved = data?.rescueCreateFosterHome ?? null;
    if (saved && !knownIds.has(saved.id)) {
      trackFosterHomeCreated();
    }

    showToast(`${input.contactFirstName} saved to your foster roster`);
  }

  /** Rejected by the backend if a pet is still placed. */
  async function handleArchiveFosterHome(home: FosterHome) {
    await archiveFosterHome({ variables: { id: home.id } });
    trackFosterHomeArchived(home.placementCount);
    showToast(`${home.fullName} archived`);
  }

  function handleOpenResend(device: Device) {
    setResendDevice(device);
    setResendOpen(true);
  }

  // `hideStalled` is set when opening from the Stalled table, where the transfer
  // is already stalled and the "move to Stalled" reason would be a no-op.
  function handleOpenCancel(device: Device, hideStalled = false) {
    setCancelDevice(device);
    setCancelHideStalledOption(hideStalled);
    setCancelOpen(true);
  }

  function handleOpenSms(device: Device) {
    setSmsDevice(device);
    setSmsOpen(true);
  }

  function handleOpenAssignPet(moduleId: string) {
    setAssignPetModuleId(moduleId);
    setAssignPetOpen(true);
  }

  // ── Mutation handlers ──────────────────────────

  function handleAddPet(_deviceId: string, petName: string) {
    void petName;
    showToast(`Pet name update is not yet supported via the API`);
  }

  // Named `transfer`, not `foster`: this page uses `foster*` for the IN_FOSTER device
  // section's own state, so a bare `foster` here would read as one of those.
  async function handleTransferToFoster(deviceId: string, transfer: FosterTransferInput) {
    const { firstName, lastName, email: fosterEmail, phone: fosterPhone, saveToRoster } = transfer;
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    // Straight from one foster home to the next: the pet has to come back to the rescue in between.
    if (device.holderType === 'foster' && !(await recallBeforeHandoff(device))) {
      return;
    }

    try {
      await createFosterTransferMut({
        variables: {
          input: {
            petId: device._petGraphqlId,
            email: fosterEmail,
            firstName,
            lastName: lastName || undefined,
            phoneNumber: fosterPhone,
            // false leaves the home off the roster, so recall tears it down.
            saveToRoster,
          },
        },
      });
      trackDeviceTransferToFoster('devices_page', { fosterSource: transfer.source, saveToRoster });
      resetAllPages();
      showToast(`${device.id} assigned to foster`);
    } catch (err) {
      showToast(`Failed to transfer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleRecall(deviceId: string, saveToRoster: boolean) {
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    if (device.holderType === 'foster') {
      // Foster recall — revoke the foster transfer
      try {
        await recallFosterTransfer({
          variables: {
            // true keeps the foster's household on the roster instead of tearing it down.
            input: { petId: device._petGraphqlId, saveToRoster },
          },
        });
        trackDeviceRecalled('devices_page', { saveToRoster });
        resetAllPages();
        showToast(`${device.id} recalled to rescue`);
      } catch (err) {
        showToast(`Failed to recall: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    } else {
      // Non-foster — just unassign the device from the pet
      try {
        await unassignDevice({
          variables: {
            moduleId: device.id,
            petId: device._petGraphqlId,
          },
        });
        resetAllPages();
        showToast(`Pet removed from ${device.id}`);
      } catch (err) {
        showToast(`Failed to remove pet: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }
  }

  async function runAdopterTransfer(
    device: Device,
    firstName: string,
    lastName: string,
    adopterEmail: string,
    adopterPhone: string,
    source: string,
    // True on the foster→adopt path, where the foster was already recalled
    // before this ran. If the transfer then fails, the pet is left in the rescue
    // household (no longer fostered), so the failure message must say so and the
    // view must refresh to reflect the recall.
    fosterRecalled = false,
  ) {
    let result;
    try {
      result = await transferPetWithChip({
        petId: device._petGraphqlId,
        chipId: device.chip?.id ?? null,
        adopter: {
          firstName,
          lastName,
          email: adopterEmail,
          phone: adopterPhone,
          street: '',
          apt: '',
          city: '',
          state: '',
          zip: '',
        },
      });
      trackDeviceTransferToAdopt(source);
      resetAllPages();
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Unknown error';
      if (fosterRecalled) {
        resetAllPages();
        showToast(
          `Foster recalled, but the adoption failed for ${device.id}: ${detail}. ${
            device.petName || 'The pet'
          } is now in your rescue's devices — retry from there.`,
        );
      } else {
        showToast(`Failed to transfer: ${detail}`);
      }
      return;
    }

    if (result.chipAssigned) {
      trackChipOwnerAdded('devices_t2a');
      showToast(`Transfer initiated for ${device.id}; chip ownership updated`);
    } else if (result.chipError) {
      const detail = result.chipError instanceof Error ? result.chipError.message : 'Unknown error';
      showToast(`Transfer initiated for ${device.id}, but chip transfer failed: ${detail}`);
    } else {
      showToast(`Transfer initiated for ${device.id}`);
    }
  }

  async function handleTransferToAdopter(
    firstName: string,
    lastName: string,
    adopterEmail: string,
    adopterPhone: string,
  ) {
    const device = adopterDevice;
    if (!device) {
      return;
    }
    const fromFoster = device.holderType === 'foster';
    if (fromFoster && !(await recallBeforeHandoff(device))) {
      return;
    }
    await runAdopterTransfer(device, firstName, lastName, adopterEmail, adopterPhone, 'devices_page', fromFoster);
  }

  // One-click adopt from the Fosters table: adopt the pet out to the foster
  // household using their existing contact info, with no modal.
  //
  // The foster placement MUST be recalled before creating the adoption transfer.
  // createPetAdoptionTransfer moves the pet out of whatever household it's in and
  // records that as the transfer's `fromHousehold`; run directly on a fostered pet
  // it would set the foster household as `fromHousehold`, strand the still-ACTIVE
  // foster transfer, and leave the foster household abandoned — so a later
  // cancel/recover would send the pet back to the foster, not the rescue. Recall
  // first returns the pet to the rescue household (ending the foster transfer),
  // which is the clean, rescue-held state the adopter transfer expects.
  /**
   * Books any of the three hand-offs. Errors are rethrown so the modal can keep itself open and show
   * them on the schedule step, rather than closing over a booking that never landed.
   */
  /**
   * Takes the pet back from its foster so a hand-off can run from the rescue's household. Used by the
   * Fosters-tab actions that move a pet straight to another foster or to an adopter. Returns false
   * when the recall failed, in which case the caller must not go on to the hand-off.
   *
   * `saveToRoster: false` matches the recall modal's unticked default and the foster-fail path.
   */
  async function recallBeforeHandoff(device: Device) {
    try {
      await recallFosterTransfer({ variables: { input: { petId: device._petGraphqlId, saveToRoster: false } } });
      trackDeviceRecalled('devices_page', { saveToRoster: false });
      return true;
    } catch (err) {
      showToast(`Failed to recall: ${err instanceof Error ? err.message : 'Unknown error'}`);
      return false;
    }
  }

  /** Opens this pet's booking on the step the rescue asked for — its date and time, or calling it off. */
  function openScheduled(device: Device, action: 'reschedule' | 'cancel') {
    const handoffId = device.scheduledHandoff?.id;
    if (!handoffId) {
      return;
    }
    setScheduledFocusId(handoffId);
    setScheduledFocusAction(action);
    setScheduledOpen(true);
  }

  const handleEditScheduled = (device: Device) => openScheduled(device, 'reschedule');
  const handleCancelScheduled = (device: Device) => openScheduled(device, 'cancel');

  async function scheduleHandoff(
    device: Device,
    kind: 'ADOPTION' | 'FOSTER' | 'ADOPTION_FROM_FOSTER',
    when: ScheduledFor,
    contact?: { firstName: string; lastName?: string; email?: string; phone?: string; saveToRoster?: boolean },
    entryPoint?: string,
  ) {
    await scheduleHandoffMut({
      variables: {
        input: {
          petId: device._petGraphqlId,
          kind,
          ...when,
          firstName: contact?.firstName,
          lastName: contact?.lastName || undefined,
          email: contact?.email || undefined,
          phoneNumber: contact?.phone || undefined,
          saveToRoster: contact?.saveToRoster,
          // Booked from the Fosters tab, so the recall is part of the booking rather than something
          // the rescue has to remember to do at the scheduled time.
          recallFirst: device.holderType === 'foster',
          // The chip moves with the adoption; the server does it after the transfer lands.
          chipId: kind === 'FOSTER' ? undefined : (device.chip?.serialNumber ?? undefined),
        },
      },
    });

    const hoursOut = Math.round((new Date(when.scheduledFor).getTime() - Date.now()) / 3_600_000);
    trackHandoffScheduled(entryPoint, { kind, hoursOut });
    showToast(`Scheduled for ${formatScheduledFor(when.scheduledFor, when.scheduledForTimezone)}`);
  }

  async function handleAdoptFromFoster(device: Device) {
    if (!isFosterContactComplete(device)) {
      showToast(`Foster contact info is incomplete — cannot start adoption`);
      return;
    }
    // Capture the foster's contact info as the adopter details. `isFosterContactComplete`
    // guarantees these are set; the `?? ''` fallbacks just satisfy the type checker
    // (property narrowing is dropped across the awaited recall below).
    const firstName = device.holderFirstName ?? '';
    const lastName = device.holderLastName ?? '';
    const email = device.holderEmail ?? '';
    const phone = device.holderPhone ?? '';

    try {
      await recallFosterTransfer({
        // Sent explicitly rather than omitted: the field defaults to true on the transfer path, so
        // leaving it out would let the request and the event below disagree with nothing to notice it.
        variables: { input: { petId: device._petGraphqlId, saveToRoster: false } },
      });
      // This flow recalls without the option, so the home survives only if it was already saved.
      trackDeviceRecalled('devices_foster_adopt', { saveToRoster: false });
    } catch (err) {
      showToast(`Failed to start adoption: ${err instanceof Error ? err.message : 'Unknown error'}`);
      return;
    }

    await runAdopterTransfer(device, firstName, lastName, email, phone, 'devices_foster_adopt', true);
  }

  // Each cancel reason resolves the pending adoption differently, so rather than
  // acting immediately we route to a per-reason confirmation modal that spells
  // out the outcome and performs the mutation once confirmed.
  function handleCancelAdoptionTransfer(device: Device, reason: CancellationReason) {
    switch (reason) {
      case 'wrong_adopter_info':
        // Cancel the transfer, then reopen the adopter screen prefilled.
        setReenterDevice(device);
        setReenterOpen(true);
        return;
      case 'device_returned':
        // Recover the device back to the rescue's unassigned stock.
        setReturnedDevice(device);
        setReturnedOpen(true);
        return;
      case 'device_kept':
        // Mark not recovered so it stays tracked in the Stalled table.
        setKeptDevice(device);
        setKeptOpen(true);
        return;
      case 'pet_and_device_returned':
        // Adoption fell through — plain cancel returns the device to inventory
        // and the pet to the rescue (same action as testing, its own trace).
        setPetDeviceReturnedDevice(device);
        setPetDeviceReturnedOpen(true);
        return;
      case 'testing':
        // No real adoption — plain cancel returns the device to inventory.
        setTestingDevice(device);
        setTestingOpen(true);
        return;
    }
  }

  async function handleConfirmTestingCancel(device: Device) {
    try {
      // No real adoption — plain cancel returns the device to inventory.
      await cancelAdoptionTransferMut({ variables: { input: { petId: device._petGraphqlId } } });
      trackDeviceCancelTransfer('devices_page', 'testing');
      resetAllPages();
      showToast(`Adoption transfer cancelled for ${device.id}`);
    } catch (err) {
      trackDeviceCancelTransferFailed('devices_page', 'testing');
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so TestingCancelModal stays open for retry.
      throw err;
    }
  }

  async function handleConfirmPetAndDeviceReturned(device: Device) {
    try {
      // Adopter brought both the pet and the collar back. A plain cancel pulls the
      // pet back into the rescue household with the collar still attached (unlike
      // recover, which unassigns the collar to bare stock). Same mutation as the
      // testing flow — it only differs in the analytics reason recorded below.
      await cancelAdoptionTransferMut({ variables: { input: { petId: device._petGraphqlId } } });
      trackDeviceCancelTransfer('devices_page', 'pet_and_device_returned');
      resetAllPages();
      showToast(`Adoption cancelled for ${device.petName ?? device.id} — back in your rescue with its collar`);
    } catch (err) {
      trackDeviceCancelTransferFailed('devices_page', 'pet_and_device_returned');
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so PetAndDeviceReturnedCancelModal stays open for retry.
      throw err;
    }
  }

  async function handleConfirmDeviceKept(device: Device) {
    try {
      // Adopter kept the collar and the rescue doesn't have it (yet). Flag the
      // still-PENDING transfer as stalled so it moves to the Stalled Transfers
      // table for follow-up, rather than resolving it now.
      await markStalledMut({ variables: { input: { petId: device._petGraphqlId } } });
      // Reason event captures the cancel-flow intent; the dedicated stalled event
      // captures the actual state transition (consistent with recovered / not-recovered).
      trackDeviceCancelTransfer('devices_page', 'device_kept');
      trackDeviceMarkedStalled('devices_page');
      resetAllPages();
      showToast(`${device.petName ?? device.id} moved to Stalled Transfers`);
    } catch (err) {
      trackDeviceCancelTransferFailed('devices_page', 'device_kept');
      showToast(`Failed to flag as stalled: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so DeviceKeptModal stays open for retry.
      throw err;
    }
  }

  async function handleConfirmDeviceReturned(device: Device) {
    try {
      // Adopter keeps the pet but handed the collar back — recover it so it
      // returns to the rescue's unassigned stock.
      await markRecoveredMut({ variables: { input: { petId: device._petGraphqlId } } });
      // Reason event captures the cancel-flow intent; the dedicated recovered event
      // keeps recovery counts consistent with the standalone Mark-Recovered flow
      // (same mutation, same signal).
      trackDeviceCancelTransfer('devices_page', 'device_returned');
      trackDeviceMarkedRecovered('devices_page');
      resetAllPages();
      showToast(`${device.id} recovered — back in your stock`);
    } catch (err) {
      trackDeviceCancelTransferFailed('devices_page', 'device_returned');
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so DeviceReturnedModal stays open for retry.
      throw err;
    }
  }

  async function handleConfirmReenterAdopter(device: Device) {
    const petId = device._petGraphqlId;
    try {
      // Bad adopter details — cancel the transfer (pet is kept) so we can reopen
      // the adoption screen prefilled and re-enter the correct info.
      await cancelAdoptionTransferMut({ variables: { input: { petId } } });
      trackDeviceCancelTransfer('devices_page', 'wrong_adopter_info');
      resetAllPages();
      showToast('Transfer cancelled — update the adopter details and resend');
      // Reopen the adopter transfer screen so the user can correct the info.
      handleOpenAdopter(device);
    } catch (err) {
      trackDeviceCancelTransferFailed('devices_page', 'wrong_adopter_info');
      showToast(`Failed to cancel: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so WrongAdopterInfoModal stays open for retry.
      throw err;
    }
  }

  // ── Stalled transfer actions ──
  //
  // Send SMS reuses the existing SMS modal (handleOpenSms).

  function handleMarkRecovered(device: Device) {
    setMarkRecoveredDevice(device);
    setMarkRecoveredOpen(true);
  }

  async function handleConfirmMarkRecovered(device: Device) {
    try {
      // Closes the transfer and unassigns the device back to the rescue's stock,
      // recording a dedicated "recovered" audit event server-side.
      await markRecoveredMut({
        variables: { input: { petId: device._petGraphqlId } },
      });
      trackDeviceMarkedRecovered('devices_page');
      resetAllPages();
      showToast(`${device.id} recovered — back in your stock`);
    } catch (err) {
      showToast(`Failed to recover device: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so MarkRecoveredModal stays open for retry.
      throw err;
    }
  }

  function handleNotRecovered(device: Device) {
    setNotRecoveredDevice(device);
    setNotRecoveredOpen(true);
  }

  async function handleConfirmNotRecovered(device: Device) {
    try {
      // Steps the rescue out of the adoption household and marks the transfer
      // NOT_RECOVERED. The collar stays attached to its pet (so it doesn't
      // return to rescue stock) but is left claimable by a future owner.
      await markNotRecoveredMut({
        variables: { input: { petId: device._petGraphqlId } },
      });
      trackDeviceMarkedNotRecovered('devices_page');
      resetAllPages();
      showToast(`${device.id} marked not recovered`);
    } catch (err) {
      showToast(`Failed to mark not recovered: ${err instanceof Error ? err.message : 'Unknown error'}`);
      // Re-throw so NotRecoveredModal stays open for retry.
      throw err;
    }
  }

  async function handleSendSms(phoneNumber: string, message: string) {
    try {
      await sendSmsMut({ variables: { input: { phoneNumber, message } } });
      trackDeviceSmsSent();
      showToast('SMS sent successfully');
    } catch (err) {
      showToast(`Failed to send SMS: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handleResendEmail(deviceId: string) {
    const device = findDevice(deviceId);
    if (!device) {
      return;
    }

    const to = device.adopterEmail ?? '';
    const subject = encodeURIComponent(`Set up your Fi collar${device.petName ? ` for ${device.petName}` : ''}`);
    const body = encodeURIComponent(
      `Hi${device.adopterName ? ` ${device.adopterName}` : ''},\n\nCongratulations on your adoption${
        device.petName ? ` of ${device.petName}` : ''
      }! Your new companion comes with a Fi GPS collar (${
        device.id
      }).\n\nTo activate the collar and start tracking, download the Fi app and follow the setup instructions:\nhttps://fitracking.com/app\n\nIf you have any questions, feel free to reach out to us or the Fi team at support@fitracking.com.\n\nBest,\n${orgName}`,
    );
    trackDeviceResendEmail();
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  }

  // ── Order Devices ──────────────────────────

  function handleOrderDevices() {
    trackDevicesOrderClicked(enrollmentData?.rescueDevicesProgram.deviceOrderAttestation.variant);
    setOrderDevicesOpen(true);
  }

  // ── Render ──────────────────────────

  if (enrollmentPending) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <LoadingSpinner />
      </>
    );
  }

  if (!isEnrolled) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <EnrollEmptyState
          description="Not enrolled yet. Add GPS tracking to your rescue program to track fosters and reward adopters."
          ctaLabel="Set up now"
          onCta={() => setEnrollModalDismissed(false)}
        />
        <DevicesEnrollModal
          open={enrollModalOpen}
          onClose={() => setEnrollModalDismissed(true)}
          onEnrolled={() => {
            setEnrollModalDismissed(true);
            refetchEnrollment();
          }}
        />
      </>
    );
  }

  // Don't blank the page out from under an open order form. OrderDevicesModal is reachable from the
  // page header, so unlike every other modal here it can be open while the device queries reload —
  // and this branch would unmount it, destroying a part-filled order or the confirmation for one
  // just placed. Falling through renders the same empty state an enrolled rescue with no devices
  // already sees, behind the modal.
  if (initialLoading && !orderDevicesOpen) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <LoadingSpinner message="Loading devices..." />
      </>
    );
  }

  if (hasError) {
    return (
      <>
        <div className={styles.pageHeader}>
          <h1>Fi Devices</h1>
        </div>
        <div className={styles.errorContainer}>
          <p>Failed to load devices. Please try again.</p>
        </div>
      </>
    );
  }

  // ── Tabs & device sub-filter ──────────────
  const tabs: TabDef[] = [
    { key: 'devices', label: 'Devices', count: unassignedCount + rescue.totalCount },
    { key: 'fosters', label: 'Fosters', count: foster.totalCount },
    { key: 'adoptions', label: 'Adoptions', count: pending.totalCount + stalled.totalCount + completed.totalCount },
  ];

  const showStock = deviceFilter === 'all' || deviceFilter === 'stock';
  const showActivated = deviceFilter === 'all' || deviceFilter === 'activated';
  const showInTransit = deviceFilter === 'all' || deviceFilter === 'inTransit';

  const adoptionsEmpty = pending.totalCount === 0 && stalled.totalCount === 0 && completed.totalCount === 0;

  return (
    <>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <h1>Fi Devices</h1>
        <div className={styles.headerActions}>
          {/* Only once something is booked: an entry point to an empty list is a dead control, and
              its presence is itself the signal that hand-offs are waiting. */}
          {scheduledCount > 0 && (
            <Button variant="ghost" onClick={() => setScheduledOpen(true)}>
              Scheduled · {scheduledCount}
            </Button>
          )}
          <Button
            variant="green"
            onClick={handleOrderDevices}
            disabled={!!pendingDeviceOrder}
            title={pendingDeviceOrder ? 'An order is awaiting approval' : undefined}
          >
            <svg
              className={styles.headerBtnIcon}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Order Devices
          </Button>
        </div>
      </div>

      {pendingDeviceOrder && (
        <div className={styles.pendingOrderBanner}>
          <svg
            className={styles.pendingOrderBannerIcon}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            <path d="M12 7.5V12L15 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span>{pendingDeviceOrder.message}</span>
        </div>
      )}

      {/* T2A Summary Stats */}
      {showStats && (
        <div className={styles.summaryCards}>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Total Pending</div>
            <div className={`${styles.summaryCardValue} ${styles.summaryValueAmber}`}>{pendingCount}</div>
            <div className={styles.summaryCardSubtext}>awaiting adopter activation</div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Transfer to Adoption Rate</div>
            <div className={`${styles.summaryCardValue} ${rateColorClass(allTimeRate)}`}>{allTimeRate}%</div>
            <div className={styles.summaryCardSubtext}>
              {completedCount} completed, {pendingCount} pending
            </div>
          </div>
          <div className={styles.summaryCard}>
            <div className={styles.summaryCardLabel}>Total Fi Pets</div>
            <div className={`${styles.summaryCardValue} ${styles.summaryValueGreen}`}>{completedCount}</div>
            <div className={styles.summaryCardSubtext}>adopted through your rescue</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <PortalTabs tabs={tabs} activeTab={currentTab} onTabChange={handleTabChange} />

      {/* Search + device sub-filters (the filters apply to the Devices tab only) */}
      <div className={styles.searchFilterRow}>
        <div className={styles.searchFilterSearch}>
          <SearchBar
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Search by device ID, pet name, or holder..."
          />
        </div>
        {currentTab === 'devices' && (
          <div className={styles.deviceFilters}>
            {DEVICE_FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`${styles.filterChip} ${deviceFilter === f.key ? styles.filterChipActive : ''}`}
                onClick={() => setDeviceFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Devices tab — Activated, Stock, In Transit */}
      {currentTab === 'devices' && (
        <>
          {showActivated && (
            <DeviceSectionTable
              title="Activated"
              description="Devices attached to pets currently in your rescue's household."
              emptyLabel="activated devices"
              showHolder={false}
              devices={rescue.devices}
              totalCount={rescue.totalCount}
              currentPage={rescue.currentPage}
              totalPages={rescue.totalPages}
              pageSize={PAGE_SIZE}
              onPageChange={rescue.goToPage}
              skeletonCount={rescue.skeletonCount}
              hasError={!!rescue.error}
              onRetry={rescue.refetch}
              searchQuery={searchQuery}
              sortKey={activatedSort?.key ?? null}
              sortDir={activatedSort?.dir}
              onSort={handleActivatedSort}
              onAddPet={handleOpenAddPet}
              onTransferFoster={handleOpenFoster}
              onRecall={handleOpenRecall}
              onTransferAdopter={handleOpenAdopter}
              onEditScheduled={handleEditScheduled}
              onCancelScheduled={handleCancelScheduled}
              onSendSms={handleOpenSms}
            />
          )}
          {showStock && (
            <StockTable
              devices={unassignedDevices}
              totalCount={unassignedCount}
              currentPage={currentStockPage}
              totalPages={stockTotalPages}
              pageSize={PAGE_SIZE}
              onPageChange={setStockPage}
              loading={inventoryLoading}
              hasError={!!inventoryError}
              onRetry={() => {
                // Swallowed deliberately: the failure is already on `inventoryError`, which is
                // what the retry row renders from. An escaping rejection would only say it twice.
                refetchInventory().catch(() => {});
              }}
              onAssign={handleOpenAssignPet}
              searchQuery={searchQuery}
            />
          )}
          {showInTransit && (
            <DevicesInTransitTable
              shipments={shipments}
              pendingOrder={pendingDeviceOrder}
              showWhenEmpty={deviceFilter === 'inTransit'}
            />
          )}
        </>
      )}

      {/* Fosters tab — placements, then the saved-foster roster */}
      {currentTab === 'fosters' && (
        <>
          <DeviceSectionTable
            title="Devices with a Foster"
            description="Devices attached to pets currently placed with a foster."
            emptyLabel="foster devices"
            showPlacedOn
            devices={foster.devices}
            totalCount={foster.totalCount}
            currentPage={foster.currentPage}
            totalPages={foster.totalPages}
            pageSize={PAGE_SIZE}
            onPageChange={foster.goToPage}
            skeletonCount={foster.skeletonCount}
            hasError={!!foster.error}
            onRetry={foster.refetch}
            searchQuery={searchQuery}
            sortKey={fosterSort.key}
            sortDir={fosterSort.dir}
            onSort={handleFosterSort}
            onAddPet={handleOpenAddPet}
            onTransferFoster={handleOpenFoster}
            onRecall={handleOpenRecall}
            onTransferAdopter={handleOpenAdopter}
            onAdoptFromFoster={handleOpenAdoptFromFoster}
            onEditScheduled={handleEditScheduled}
            onCancelScheduled={handleCancelScheduled}
            onViewFoster={roster.openDetail}
            fosterHomes={fosterHomes.homes}
            onSendSms={handleOpenSms}
          />
          <FosterRosterTable
            homes={rosterPage.homes}
            totalCount={rosterPage.totalCount}
            currentPage={rosterPage.currentPage}
            totalPages={rosterPage.totalPages}
            loading={rosterPage.loading}
            hasError={rosterPage.error}
            onRetry={rosterPage.refetch}
            pageSize={PAGE_SIZE}
            onPageChange={rosterPage.goToPage}
            searchQuery={searchQuery}
            sortKey={rosterSort?.key ?? null}
            sortDir={rosterSort?.dir}
            onSort={handleRosterSort}
            onView={roster.openDetail}
            onAdd={() => roster.setAddOpen(true)}
          />
        </>
      )}

      {/* Adoptions tab — Pending, Completed, Stalled */}
      {currentTab === 'adoptions' && (
        <>
          <PendingTransfersTable
            devices={pending.devices}
            totalCount={pending.totalCount}
            currentPage={pending.currentPage}
            totalPages={pending.totalPages}
            pageSize={PAGE_SIZE}
            onPageChange={pending.goToPage}
            loading={pending.loading}
            skeletonCount={pending.skeletonCount}
            sortKey={pendingSort?.key ?? null}
            sortDir={pendingSort?.dir}
            onSort={handlePendingSort}
            onResendEmail={handleOpenResend}
            onCancelTransfer={handleOpenCancel}
            onSendSms={handleOpenSms}
          />
          <CompletedTransfersTable
            devices={completed.devices}
            totalCount={completed.totalCount}
            currentPage={completed.currentPage}
            totalPages={completed.totalPages}
            pageSize={PAGE_SIZE}
            onPageChange={completed.goToPage}
            loading={completed.loading}
            skeletonCount={completed.skeletonCount}
          />
          <StalledTransfersTable
            devices={stalled.devices}
            totalCount={stalled.totalCount}
            currentPage={stalled.currentPage}
            totalPages={stalled.totalPages}
            pageSize={PAGE_SIZE}
            thresholdDays={stalledThresholdDays}
            onPageChange={stalled.goToPage}
            skeletonCount={stalled.skeletonCount}
            sortKey={stalledSort.key}
            sortDir={stalledSort.dir}
            onSort={handleStalledSort}
            onMarkRecovered={handleMarkRecovered}
            onNotRecovered={handleNotRecovered}
            onSendSms={handleOpenSms}
            onCancelTransfer={(device) => handleOpenCancel(device, true)}
          />
          {/*
            All three sections, not two: they are three independent round-trips now, and the
            slowest one still loading is the one whose rows would contradict this copy.
          */}
          {adoptionsEmpty &&
            pending.skeletonCount === 0 &&
            stalled.skeletonCount === 0 &&
            completed.skeletonCount === 0 && (
              <div className={styles.tabEmptyState}>
                No adoptions to show{searchQuery ? ` for "${searchQuery}"` : ''}.
              </div>
            )}
        </>
      )}

      {/* Modals */}
      <AddPetModal
        open={addPetOpen}
        onClose={() => setAddPetOpen(false)}
        device={addPetDevice}
        onAddPet={handleAddPet}
      />
      <TransferToFosterModal
        open={fosterOpen}
        onClose={() => setFosterOpen(false)}
        deviceId={fosterDevice?.id ?? null}
        petName={fosterDevice?.petName ?? null}
        homes={fosterHomes.homes}
        homesLoaded={fosterHomes.loaded}
        recallFirst={fosterDevice?.holderType === 'foster'}
        excludeHomeId={
          fosterDevice?.holderType === 'foster'
            ? (findFosterHomeForDevice(fosterDevice, fosterHomes.homes)?.id ?? null)
            : null
        }
        onTransfer={(transfer) => {
          if (fosterDevice) {
            handleTransferToFoster(fosterDevice.id, transfer);
          }
        }}
        onSchedule={async (transfer, when) => {
          if (fosterDevice) {
            await scheduleHandoff(fosterDevice, 'FOSTER', when, transfer, 'devices_page');
          }
        }}
      />
      <RecallDeviceModal
        open={recallOpen}
        onClose={() => setRecallOpen(false)}
        device={recallDevice}
        homes={fosterHomes.homes}
        homesLoaded={fosterHomes.loaded}
        onRecall={(saveToRoster) => {
          if (recallDevice) {
            handleRecall(recallDevice.id, saveToRoster);
          }
        }}
      />
      <AdoptFromFosterModal
        open={adoptFromFosterOpen}
        onClose={() => {
          setAdoptFromFosterOpen(false);
          setAdoptFromFosterDevice(null);
        }}
        device={adoptFromFosterDevice}
        homes={fosterHomes.homes}
        onConfirm={handleAdoptFromFoster}
        onSchedule={async (device, when) => {
          // The adopter is the current foster, so the server reads that contact off the open foster
          // transfer rather than taking it from here.
          await scheduleHandoff(device, 'ADOPTION_FROM_FOSTER', when, undefined, 'devices_foster_adopt');
        }}
      />

      <ScheduledHandoffsModal
        open={scheduledOpen}
        focusHandoffId={scheduledFocusId}
        focusAction={scheduledFocusAction}
        onClose={() => {
          setScheduledOpen(false);
          setScheduledFocusId(null);
        }}
      />

      <FosterHomeDetailsModal
        open={roster.detailOpen}
        onClose={roster.closeDetail}
        home={roster.detailHome}
        onArchive={handleArchiveFosterHome}
        viewablePlacementIds={roster.viewablePlacementIds}
        onViewPlacementPet={roster.viewPlacementPet}
        loadingPlacementId={roster.loadingPlacementId}
      />

      {/* Read-only: pet edits belong on the Dashboard, which owns those flows. */}
      <ViewPetModal
        open={!!roster.placementPet}
        onClose={roster.closePlacementPet}
        pet={roster.placementPet ? { ...roster.placementPet, status: 'foster' } : null}
      />

      <AddFosterModal
        open={roster.addOpen}
        onClose={() => roster.setAddOpen(false)}
        onCreate={handleCreateFosterHome}
      />
      <TransferToAdopterModal
        open={adopterOpen}
        onClose={() => {
          setAdopterOpen(false);
          setAdopterDevice(null);
        }}
        deviceId={adopterDevice?.id ?? ''}
        petName={adopterDevice?.petName ?? null}
        chipSerialNumber={adopterDevice?.chip?.serialNumber ?? null}
        showRescueDiscount
        initialFirstName={adopterDevice?.adopterFirstName ?? null}
        initialLastName={adopterDevice?.adopterLastName ?? null}
        initialEmail={adopterDevice?.adopterEmail ?? null}
        initialPhone={adopterDevice?.adopterPhone ?? null}
        recallFirst={adopterDevice?.holderType === 'foster'}
        onTransfer={handleTransferToAdopter}
        onSchedule={async (details, when) => {
          if (adopterDevice) {
            await scheduleHandoff(adopterDevice, 'ADOPTION', when, details, 'devices_t2a');
          }
        }}
      />
      <ResendEmailModal
        open={resendOpen}
        onClose={() => setResendOpen(false)}
        device={resendDevice}
        onResend={handleResendEmail}
      />
      <CancelTransferModal
        open={cancelOpen}
        onClose={() => {
          setCancelOpen(false);
          setCancelDevice(null);
        }}
        device={cancelDevice}
        onConfirm={handleCancelAdoptionTransfer}
        hideStalledOption={cancelHideStalledOption}
      />
      <WrongAdopterInfoModal
        open={reenterOpen}
        onClose={() => {
          setReenterOpen(false);
          setReenterDevice(null);
        }}
        onBack={() => {
          // Return to the cancel-reason picker with the same device. The transfer
          // hasn't been cancelled yet, so nothing is lost.
          setReenterOpen(false);
          setCancelDevice(reenterDevice);
          setCancelOpen(true);
          setReenterDevice(null);
        }}
        device={reenterDevice}
        onConfirm={handleConfirmReenterAdopter}
      />
      <DeviceReturnedModal
        open={returnedOpen}
        onClose={() => {
          setReturnedOpen(false);
          setReturnedDevice(null);
        }}
        onBack={() => {
          // Return to the cancel-reason picker with the same device. The transfer
          // hasn't been recovered yet, so nothing is lost.
          setReturnedOpen(false);
          setCancelDevice(returnedDevice);
          setCancelOpen(true);
          setReturnedDevice(null);
        }}
        device={returnedDevice}
        onConfirm={handleConfirmDeviceReturned}
      />
      <DeviceKeptModal
        open={keptOpen}
        onClose={() => {
          setKeptOpen(false);
          setKeptDevice(null);
        }}
        onBack={() => {
          // Return to the cancel-reason picker with the same device. The transfer
          // hasn't been resolved yet, so nothing is lost.
          setKeptOpen(false);
          setCancelDevice(keptDevice);
          setCancelOpen(true);
          setKeptDevice(null);
        }}
        device={keptDevice}
        onConfirm={handleConfirmDeviceKept}
      />
      <TestingCancelModal
        open={testingOpen}
        onClose={() => {
          setTestingOpen(false);
          setTestingDevice(null);
        }}
        onBack={() => {
          // Return to the cancel-reason picker with the same device. The transfer
          // hasn't been cancelled yet, so nothing is lost.
          setTestingOpen(false);
          setCancelDevice(testingDevice);
          setCancelOpen(true);
          setTestingDevice(null);
        }}
        device={testingDevice}
        onConfirm={handleConfirmTestingCancel}
      />
      <PetAndDeviceReturnedCancelModal
        open={petDeviceReturnedOpen}
        onClose={() => {
          setPetDeviceReturnedOpen(false);
          setPetDeviceReturnedDevice(null);
        }}
        onBack={() => {
          // Return to the cancel-reason picker with the same device. The transfer
          // hasn't been cancelled yet, so nothing is lost.
          setPetDeviceReturnedOpen(false);
          setCancelDevice(petDeviceReturnedDevice);
          setCancelOpen(true);
          setPetDeviceReturnedDevice(null);
        }}
        device={petDeviceReturnedDevice}
        onConfirm={handleConfirmPetAndDeviceReturned}
      />
      <SendSmsModal
        open={smsOpen}
        onClose={() => setSmsOpen(false)}
        device={smsDevice}
        orgName={orgName}
        onSendSms={handleSendSms}
      />
      <MarkRecoveredModal
        open={markRecoveredOpen}
        onClose={() => {
          setMarkRecoveredOpen(false);
          setMarkRecoveredDevice(null);
        }}
        device={markRecoveredDevice}
        onConfirm={handleConfirmMarkRecovered}
      />
      <NotRecoveredModal
        open={notRecoveredOpen}
        onClose={() => {
          setNotRecoveredOpen(false);
          setNotRecoveredDevice(null);
        }}
        device={notRecoveredDevice}
        onConfirm={handleConfirmNotRecovered}
      />
      <AddDeviceToPetModal
        open={assignPetOpen}
        onClose={() => {
          setAssignPetOpen(false);
          setAssignPetModuleId(null);
        }}
        moduleId={assignPetModuleId}
      />
      <OrderDevicesModal open={orderDevicesOpen} onClose={() => setOrderDevicesOpen(false)} />
    </>
  );
}
